import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from "react-router-dom";
import { Spin } from "antd";
import { baseUrl } from '../../constants/api';
import { RootState, setTopics, setQuizzes, setQuestion, setTimeRemaining } from '../../store';
import TopicSelector from "../../components/Shared/TopicSelector";
import SubtopicSelector from "../../components/Shared/SubtopicSelector";
import DashboardCharts from "../../components/Shared/DashboardCharts";
import QuizCard from "../../components/Shared/QuizCard";
import showToastMessage from "../../utils/showToastMessage";
import { FullScreenContext } from "../../App";

interface Quiz {
    quiz_id: string;
    quiz_name: string;
    number_of_questions: number;
    quiz_duration: string;
    createdAt: string;
    appeared_on?: string;
    availability: boolean;
    reattempt: boolean;
    score: number | null;
    quiz_type?: "regular" | "certification";
}

export default function UserAnalytics() {
    const { participant_id } = useParams<{ participant_id?: string }>();
    const token = useSelector((state: RootState) => state.account.token);
    const profile = useSelector((state: RootState) => state.account.profile);
    const userId = useSelector((state: RootState) => state.account.user_id);
    const targetUserId = participant_id || profile?.id || profile?._id || userId;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { handle } = useContext(FullScreenContext);

    // Topics & Quizzes state mirroring QuizList
    const { topics, quizzes } = useSelector((state: RootState) => state.quiz);
    const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
    const [selectedSubtopic, setSelectedSubtopic] = useState<string>("All");
    const [loadingQuizzes, setLoadingQuizzes] = useState(false);
    const [analyticsView, setAnalyticsView] = useState<"regular" | "certification">("regular");

    // Fetch Topics and Quizzes logic
    const fetchTopics = useCallback(async () => {
        const apiUrl = `${baseUrl}/api/topic/get-all-topics`;
        try {
            const response = await fetch(apiUrl, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const topicData = await response.json();
                dispatch(setTopics(topicData.data));
                if (topicData.data.length > 0 && !selectedTopic) {
                    setSelectedTopic(topicData.data[0].topic_name);
                }
            } else {
                showToastMessage(`Failed to fetch topics`, "error");
            }
        } catch (error) {
            showToastMessage("An error occurred while fetching topics.", "error");
        }
    }, [token, dispatch, selectedTopic]);

    const fetchQuizzes = useCallback(async (topicName: string, subtopicName: string) => {
        let apiUrl = `${baseUrl}/api/quiz/get-quizes?topic_name=${topicName}&subtopic_name=${subtopicName}`;
        if (participant_id) {
            apiUrl += `&user_id=${participant_id}`;
        }
        setLoadingQuizzes(true);
        try {
            const response = await fetch(apiUrl, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const quizData = await response.json();
                if (quizData?.quizes) {
                    dispatch(setQuizzes(quizData?.quizes));
                } else {
                    dispatch(setQuizzes([]));
                }
            } else {
                showToastMessage(`Failed to fetch quizzes`, "error");
            }
        } catch (error) {
            showToastMessage("An error occurred while fetching quizzes.", "error");
        } finally {
            setLoadingQuizzes(false);
        }
    }, [token, dispatch, participant_id]);

    useEffect(() => {
        fetchTopics();
    }, [fetchTopics]);

    useEffect(() => {
        if (selectedTopic) {
            fetchQuizzes(selectedTopic, selectedSubtopic);
        }
    }, [selectedTopic, selectedSubtopic, fetchQuizzes]);

    const handleTopicSelect = (topic: string | null) => {
        setSelectedTopic(topic);
        setSelectedSubtopic("All");
        dispatch(setQuizzes([]));
    };

    const handleSubtopicSelect = (subtopic: string | null) => {
        if (subtopic) setSelectedSubtopic(subtopic);
    };

    const currentSubtopics = useMemo(() => {
        const topic = topics.find((t: any) => t.topic_name === selectedTopic);
        return topic ? ["All", ...topic.subtopics] : ["All"];
    }, [topics, selectedTopic]);

    const fetchQuestions = async (quizId: string) => {
        const apiUrl = `${baseUrl}/api/quiz/get-questions/${quizId}`;
        try {
            const response = await fetch(apiUrl, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const questionData = await response.json();
                const selectedQuiz = (quizzes as Quiz[]).find((quiz) => quiz.quiz_id === quizId);
                dispatch(setQuestion({
                    questions: questionData.questions,
                    quiz_duration: selectedQuiz ? selectedQuiz.quiz_duration : undefined
                }));
                if (selectedQuiz) {
                    dispatch(setTimeRemaining(parseInt(selectedQuiz.quiz_duration) * 60));
                }
            } else {
                showToastMessage(`Failed to fetch questions`, "error");
            }
        } catch (error) {
            showToastMessage("An error occurred while fetching questions.", "error");
        }
    };

    const handleStartQuiz = (quizId: string) => {
        const selectedQuiz = (quizzes as Quiz[]).find((quiz) => quiz.quiz_id === quizId);
        if (selectedQuiz) {
            dispatch(setTimeRemaining(parseInt(selectedQuiz.quiz_duration) * 60));
        }
        setTimeout(() => {
            navigate(`/quiz/${quizId}/start`);
            if (handle) {
                handle.enter();
            }
            fetchQuestions(quizId);
        }, 100);
    };

    const { completedQuizzes, completedCertificationQuizzes } = useMemo(() => {
        const qList = (quizzes as Quiz[]) || [];
        const isAttempted = (quiz: Quiz) => quiz.score !== null || Boolean(quiz.appeared_on);
        return {
            completedQuizzes: qList.filter(q => (isAttempted(q) || q.reattempt) && q.quiz_type !== "certification"),
            completedCertificationQuizzes: qList.filter(q => (isAttempted(q) || q.reattempt) && q.quiz_type === "certification")
        };
    }, [quizzes]);

    const renderQuizCard = (quiz: Quiz, isCompleted: boolean, index: number) => {
        const actions = (
            <>
                {isCompleted && quiz.score !== null && (
                    <div className="flex flex-col items-end gap-2">
                        <span className="text-xl font-bold text-[#111]">Score : <span className="text-[#111]">{Math.round(quiz.score * 100) / 100}</span></span>
                        {quiz.reattempt && !participant_id ? (
                            <button className="flex items-center gap-1.5 bg-none border-none font-semibold cursor-pointer text-sm transition-all duration-200 hover:opacity-70 text-[#D97706]" onClick={() => handleStartQuiz(quiz.quiz_id)}>
                                Redo Quiz <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 4v6h-6" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></svg>
                            </button>
                        ) : (
                            <button className="flex items-center gap-1.5 bg-none border-none font-semibold cursor-pointer text-sm transition-all duration-200 hover:opacity-70 text-[#374151]" onClick={() => navigate(`/quiz/${quiz.quiz_id}/review${participant_id ? `?user_id=${participant_id}` : ''}`)}>
                                View Quiz <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                            </button>
                        )}
                    </div>
                )}
            </>
        );

        return (
            <QuizCard
                key={quiz.quiz_id}
                quiz={quiz as any}
                index={index}
                isCompleted={isCompleted}
                actions={actions}
            />
        );
    };

    return (
        <div className="max-w-[1200px] w-full px-5 pb-4 md:px-10 bg-white rounded-2xl mx-auto shadow-sm">
            <TopicSelector
                topics={topics}
                selectedTopic={selectedTopic}
                handleTopicSelect={handleTopicSelect}
            />
            <div className='h-[10px]' />
            <div className="dashboard-mode-switch mb-4">
                <button
                    type="button"
                    className={`dashboard-mode-btn ${analyticsView === "regular" ? "active" : ""}`}
                    onClick={() => setAnalyticsView("regular")}
                >
                    Progress Dashboard
                </button>
                <button
                    type="button"
                    className={`dashboard-mode-btn ${analyticsView === "certification" ? "active certification-tab-active" : ""}`}
                    onClick={() => setAnalyticsView("certification")}
                >
                    Certification
                </button>
            </div>
            <SubtopicSelector
                subtopics={currentSubtopics}
                selectedSubtopic={selectedSubtopic}
                handleSubtopicSelect={handleSubtopicSelect}
            />

            {analyticsView === "regular" && (
                <div className="mb-8 p-4 border rounded shadow-sm bg-white m-0">
                    <DashboardCharts
                        token={token}
                        topics={topics}
                        selectedTopic={selectedTopic}
                        selectedSubtopic={selectedSubtopic}
                        userId={targetUserId}
                    />
                </div>
            )}

            {loadingQuizzes ? (
                <div className="flex justify-center items-center py-10">
                    <Spin size="large" />
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {analyticsView === "regular" && completedQuizzes.length > 0 && (
                        <>
                            <h3 className="text-xl font-bold text-[#111] mb-2 mt-5">Past Quizzes</h3>
                            {completedQuizzes.map((q: any, i: number) => renderQuizCard(q, true, i))}
                        </>
                    )}
                    {analyticsView === "certification" && (
                        <div className="dashboard-chart-card certification-chart-card mt-5">
                            <div className="dashboard-chart-card-title">Certification Results</div>
                            <div className="dashboard-chart-card-subtitle">Completed certification attempts for the selected topic and subtopic.</div>
                            {completedCertificationQuizzes.length > 0 ? (
                                <div className="flex flex-col gap-3">
                                    {completedCertificationQuizzes.map((q: any, i: number) => renderQuizCard(q, true, i))}
                                </div>
                            ) : (
                                <div className="text-sm text-[#6b7280] py-6">No certification attempts found for the selected filters.</div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
